import { Popover } from "@base-ui/react/popover"
import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { Controller, useForm } from "react-hook-form"

import Button from "src/components/Button"
import DiscardConfirmModal from "src/components/DiscardConfirmModal"
import Form from "src/components/Form"

import { useDiscardConfirm } from "src/hooks/use_discard_confirm"

const MAX_REASON_LENGTH = 300

const HoldReasonPopover = ({ triggerLabel, onHold, disabled }) => {
  const [open, setOpen] = useState(false)
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm({ defaultValues: { holdReason: "" } })

  const closePopover = () => {
    setOpen(false)
    reset({ holdReason: "" })
  }

  const { handleCloseRequest, discardConfirmProps } = useDiscardConfirm({
    isDirty,
    onClose: closePopover,
  })

  const handleOpenChange = (next) => {
    if (next) {
      setOpen(true)
      return
    }
    handleCloseRequest()
  }

  const onSubmit = ({ holdReason }) => {
    onHold(holdReason.trim(), { onSuccess: closePopover })
  }

  return (
    <>
      <Popover.Root open={open} onOpenChange={handleOpenChange}>
        <Popover.Trigger
          type="button"
          disabled={disabled}
          className={classNames(
            "btn btn-secondary-yellow btn-sm flex items-center justify-center whitespace-nowrap text-center focus:outline-none focus:ring print:hidden",
            { "opacity-50": disabled }
          )}
        >
          <span>
            <i className="icon icon-pause inline" aria-hidden="true" />
            <span>&nbsp;&nbsp;</span>
          </span>
          {triggerLabel}
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner
            side="bottom"
            align="end"
            sideOffset={4}
            className="z-50"
          >
            <Popover.Popup
              className="w-80 rounded border bg-white p-4 shadow-lg ring-1 ring-black ring-opacity-5"
              initialFocus={false}
            >
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Label htmlFor="hold_reason">
                  Why is this job on hold?
                </Form.Label>
                <Controller
                  name="holdReason"
                  control={control}
                  rules={{
                    validate: (value) =>
                      value.trim().length > 0 ||
                      "Provide a reason for putting this job on hold.",
                  }}
                  render={({ field }) => (
                    <Form.Textarea
                      id="hold_reason"
                      rows={3}
                      maxLength={MAX_REASON_LENGTH}
                      hasErrors={Boolean(errors.holdReason)}
                      {...field}
                    />
                  )}
                />
                {errors.holdReason && (
                  <Form.Error>{errors.holdReason.message}</Form.Error>
                )}
                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    small
                    onClick={handleCloseRequest}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" small>
                    Confirm
                  </Button>
                </div>
              </Form>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>

      <DiscardConfirmModal {...discardConfirmProps} />
    </>
  )
}

HoldReasonPopover.propTypes = {
  triggerLabel: PropTypes.string.isRequired,
  onHold: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

HoldReasonPopover.defaultProps = {
  disabled: false,
}

export default HoldReasonPopover
